/**
 * Support Diagnostics Page - Phase 14
 * 
 * Allows authorized users to generate and export diagnostics bundles.
 */

import React, { useState, useEffect, useCallback } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { 
  FileJson, 
  FileSpreadsheet, 
  Download, 
  RefreshCw, 
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wifi,
  WifiOff,
  Server,
  Clock,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { 
  getDiagnosticsBundle,
  getQueueDiagnostics,
  getIncidentDiagnostics,
  getNetworkDiagnostics,
  getAppContextDiagnostics
} from '@/lib/supportDiagnostics/diagnosticsStore';
import {
  exportDiagnosticsBundleJson,
  exportDiagnosticsSummaryCsv
} from '@/lib/supportDiagnostics/exportEngine';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

export default function SupportDiagnosticsPage() {
  const [loading, setLoading] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [scope, setScope] = useState({
    merchantId: null,
    locationId: null,
    terminalId: null,
    dateFrom: null,
    dateTo: null
  });
  const [activeTab, setActiveTab] = useState('overview');
  
  // Quick diagnostics (lightweight, no full bundle)
  const [quickDiagnostics, setQuickDiagnostics] = useState({
    queue: null,
    incidents: null,
    network: null,
    app: null
  });

  const loadQuickDiagnostics = useLatestCallback(async () => {
    const [queue, incidents, network, app] = await Promise.all([
      getQueueDiagnostics(scope),
      getIncidentDiagnostics(scope),
      getNetworkDiagnostics(),
      getAppContextDiagnostics()
    ]);
    
    setQuickDiagnostics({ queue, incidents, network, app });
  });

  // Load quick diagnostics on mount
  useEffect(() => {
    loadQuickDiagnostics();
  }, [loadQuickDiagnostics]);

  const generateBundle = useCallback(async () => {
    setLoading(true);
    try {
      const user = {
        userId: localStorage.getItem('user_id') || 'unknown',
        role: localStorage.getItem('user_role') || 'admin'
      };
      
      const result = await getDiagnosticsBundle(scope, user);
      setBundle(result);
    } catch (error) {
      console.error('Failed to generate diagnostics bundle:', error);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const handleExportJson = () => {
    if (bundle) {
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `diagnostics-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      exportDiagnosticsBundleJson(scope);
    }
  };

  const handleExportCsv = () => {
    exportDiagnosticsSummaryCsv(scope);
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
    return `${(ms / 3600000).toFixed(1)}h`;
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="support-diagnostics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Support Diagnostics
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Generate operational snapshots for support and troubleshooting
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadQuickDiagnostics}
            data-testid="refresh-diagnostics-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Network Status */}
        <Card data-testid="network-status-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              {quickDiagnostics.network?.online ? (
                <Wifi className="h-8 w-8 text-green-500" />
              ) : (
                <WifiOff className="h-8 w-8 text-red-500" />
              )}
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Network</p>
                <p className="text-lg font-semibold">
                  {quickDiagnostics.network?.online ? 'Online' : 'Offline'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Queue Status */}
        <Card data-testid="queue-status-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Server className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Pending Queue</p>
                <p className="text-lg font-semibold">
                  {quickDiagnostics.queue?.snapshot?.pendingCount ?? '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Open Incidents */}
        <Card data-testid="incidents-status-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`h-8 w-8 ${
                quickDiagnostics.incidents?.open > 0 ? 'text-yellow-500' : 'text-green-500'
              }`} />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Open Incidents</p>
                <p className="text-lg font-semibold">
                  {quickDiagnostics.incidents?.open ?? '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Critical Count */}
        <Card data-testid="critical-status-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              {quickDiagnostics.incidents?.critical > 0 ? (
                <XCircle className="h-8 w-8 text-red-500" />
              ) : (
                <CheckCircle className="h-8 w-8 text-green-500" />
              )}
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Critical</p>
                <p className="text-lg font-semibold">
                  {quickDiagnostics.incidents?.critical ?? '0'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scope Selector */}
      <Card data-testid="scope-selector-card">
        <CardHeader>
          <CardTitle className="text-lg">Scope Selection</CardTitle>
          <CardDescription>
            Filter diagnostics by merchant, location, or terminal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Location ID</Label>
              <Input
                placeholder="Enter location ID (optional)"
                value={scope.locationId || ''}
                onChange={(e) => setScope({ ...scope, locationId: e.target.value || null })}
                data-testid="scope-location-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Terminal ID</Label>
              <Input
                placeholder="Enter terminal ID (optional)"
                value={scope.terminalId || ''}
                onChange={(e) => setScope({ ...scope, terminalId: e.target.value || null })}
                data-testid="scope-terminal-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Date Range</Label>
              <Select
                value={scope.dateFrom ? 'custom' : 'all'}
                onValueChange={(val) => {
                  if (val === 'all') {
                    setScope({ ...scope, dateFrom: null, dateTo: null });
                  } else if (val === '24h') {
                    setScope({ ...scope, dateFrom: Date.now() - 86400000, dateTo: Date.now() });
                  } else if (val === '7d') {
                    setScope({ ...scope, dateFrom: Date.now() - 604800000, dateTo: Date.now() });
                  }
                }}
              >
                <SelectTrigger data-testid="scope-date-select">
                  <SelectValue placeholder="All time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="24h">Last 24 hours</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="mt-4 flex gap-2">
            <Button onClick={generateBundle} disabled={loading} data-testid="generate-bundle-btn">
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Activity className="h-4 w-4 mr-2" />
                  Generate Full Bundle
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bundle Preview / Export */}
      {bundle && (
        <Card data-testid="bundle-preview-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-lg">Diagnostics Bundle</CardTitle>
                <CardDescription>
                  Generated at {new Date(bundle.generatedAt).toLocaleString()}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleExportJson} data-testid="export-json-btn">
                  <FileJson className="h-4 w-4 mr-2" />
                  Export JSON
                </Button>
                <Button variant="outline" onClick={handleExportCsv} data-testid="export-csv-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="queue">Queue</TabsTrigger>
                <TabsTrigger value="incidents">Incidents</TabsTrigger>
                <TabsTrigger value="pos">POS Recovery</TabsTrigger>
                <TabsTrigger value="network">Network</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">App Version</p>
                    <p className="text-lg font-medium">{bundle.appContext?.appVersion || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">Environment</p>
                    <p className="text-lg font-medium">{bundle.appContext?.environment || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">Browser</p>
                    <p className="text-lg font-medium">{bundle.appContext?.browser || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">Device</p>
                    <p className="text-lg font-medium">{bundle.appContext?.deviceType || 'N/A'}</p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="queue" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {bundle.queueSnapshot?.pendingCount || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Failed</p>
                    <p className="text-2xl font-bold text-red-600">
                      {bundle.queueSnapshot?.failedCount || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Conflicts</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {bundle.queueSnapshot?.conflictCount || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">Oldest Pending</p>
                    <p className="text-lg font-medium">
                      {formatDuration(bundle.queueSnapshot?.oldestPendingAgeMs)}
                    </p>
                  </div>
                </div>
                
                {bundle.queueSnapshot?.byDomain && Object.keys(bundle.queueSnapshot.byDomain).length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-2">By Domain</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(bundle.queueSnapshot.byDomain).map(([domain, count]) => (
                        <Badge key={domain} variant="outline">
                          {domain}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="incidents" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Open</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {bundle.incidentSummary?.openCount || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Critical</p>
                    <p className="text-2xl font-bold text-red-600">
                      {bundle.incidentSummary?.criticalCount || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Blocked Payments</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {bundle.incidentSummary?.blockedPaymentCount || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Escalated</p>
                    <p className="text-lg font-bold text-blue-600">
                      {bundle.incidentSummary?.escalatedCount || 0}
                    </p>
                  </div>
                </div>
                
                {bundle.incidentSummary?.unresolvedIncidents?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-2">Unresolved Incidents</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {bundle.incidentSummary.unresolvedIncidents.slice(0, 10).map((inc) => (
                        <div
                          key={inc.incidentId}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-sm">{inc.title}</p>
                            <p className="text-xs text-gray-500">
                              {inc.domain} | {new Date(inc.occurredAt).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant={inc.severity === 'critical' ? 'destructive' : 'secondary'}>
                            {inc.severity}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="pos" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Unsynced Orders</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {bundle.posRecoverySummary?.unsyncedOrders || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Blocked Payments</p>
                    <p className="text-2xl font-bold text-red-600">
                      {bundle.posRecoverySummary?.blockedPayments || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Failed Kitchen</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {bundle.posRecoverySummary?.failedKitchen || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">Failed Receipts</p>
                    <p className="text-2xl font-bold">
                      {bundle.posRecoverySummary?.failedReceipts || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-sm text-gray-500">Stale Items</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {bundle.posRecoverySummary?.staleItems || 0}
                    </p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="network" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg ${
                    bundle.networkDiagnostics?.currentStatus === 'online'
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-red-50 dark:bg-red-900/20'
                  }`}>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className={`text-lg font-bold ${
                      bundle.networkDiagnostics?.currentStatus === 'online'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {bundle.networkDiagnostics?.currentStatus || 'Unknown'}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${
                    bundle.networkDiagnostics?.backendReachable
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-yellow-50 dark:bg-yellow-900/20'
                  }`}>
                    <p className="text-sm text-gray-500">Backend Reachable</p>
                    <p className="text-lg font-bold">
                      {bundle.networkDiagnostics?.backendReachable ? 'Yes' : 'No/Unknown'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">Connection Type</p>
                    <p className="text-lg font-medium">
                      {bundle.networkDiagnostics?.effectiveConnection?.type || 'N/A'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-500">Last Health Check</p>
                    <p className="text-sm font-medium">
                      {bundle.networkDiagnostics?.lastHealthCheck?.checkedAt
                        ? new Date(bundle.networkDiagnostics.lastHealthCheck.checkedAt).toLocaleTimeString()
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Export Actions */}
      <Card data-testid="export-actions-card">
        <CardHeader>
          <CardTitle className="text-lg">Quick Export</CardTitle>
          <CardDescription>
            Export diagnostics without generating full bundle preview
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={handleExportJson} data-testid="quick-export-json-btn">
              <FileJson className="h-4 w-4 mr-2" />
              Export Full JSON
            </Button>
            <Button variant="outline" onClick={handleExportCsv} data-testid="quick-export-csv-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export Summary CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cross-links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Related Tools</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href={bo('incident-audit')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-incident-audit"
            >
              <div>
                <p className="font-medium">Incident Audit Trail</p>
                <p className="text-sm text-gray-500">View detailed incident history</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </a>
            
            <a
              href={bo('reliability-reporting')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-reliability-reporting"
            >
              <div>
                <p className="font-medium">Reliability Reporting</p>
                <p className="text-sm text-gray-500">View reliability metrics & trends</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </a>
            
            <a
              href={bo('pos-reconciliation')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-pos-reconciliation"
            >
              <div>
                <p className="font-medium">POS Reconciliation</p>
                <p className="text-sm text-gray-500">Review POS offline recovery</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
